import { Label } from 'cpool-components/general/Label';
import { SegmentedControl } from 'cpool-components/input/SegmentedControl';
import type { Control, FieldValues, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormField } from './FormField';

const BOOLEAN_OPTION_VALUES = {
    NO: 'NO',
    YES: 'YES',
} as const;

type BooleanOptionValue = (typeof BOOLEAN_OPTION_VALUES)[keyof typeof BOOLEAN_OPTION_VALUES];

type FormBooleanSegmentedControlProps<TFieldValues extends FieldValues> = {
    control: Control<TFieldValues>;
    disabled?: boolean;
    id: Path<TFieldValues>;
    label: string;
    labelClassName?: string;
};

const toOptionValue = (value: boolean | null | undefined): BooleanOptionValue | null => {
    if (value === true) return BOOLEAN_OPTION_VALUES.YES;
    if (value === false) return BOOLEAN_OPTION_VALUES.NO;
    return null;
};

export const FormBooleanSegmentedControl = <TFieldValues extends FieldValues>({
    control,
    disabled,
    id,
    label,
    labelClassName = 'h-8 w-[175px] shrink-0 justify-start',
}: FormBooleanSegmentedControlProps<TFieldValues>) => {
    const { t } = useTranslation();

    return (
        <FormField<TFieldValues, boolean | null> control={control} name={id}>
            {({ error, onChange, value }) => (
                <div className="flex items-start gap-2.5">
                    <Label htmlFor={id} className={labelClassName}>
                        {label}
                    </Label>
                    <SegmentedControl<BooleanOptionValue>
                        id={id}
                        ariaLabel={label}
                        options={[
                            { label: t('VALUES.YES'), value: BOOLEAN_OPTION_VALUES.YES },
                            { label: t('VALUES.NO'), value: BOOLEAN_OPTION_VALUES.NO },
                        ]}
                        value={toOptionValue(value)}
                        onChange={(newValue) => onChange(newValue === BOOLEAN_OPTION_VALUES.YES)}
                        disabled={disabled}
                        error={error}
                    />
                </div>
            )}
        </FormField>
    );
};
